import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  X, Clock, Users, Flame, Beef, Wheat, Droplet, Plus, Check, Loader2,
  ListChecks, ChefHat, Heart, TrendingUp, NotebookPen, Share2, MessageCircle, Copy, Save, Trash2, Utensils, Download,
} from 'lucide-react';
import { MEALS, todayStr } from './trackingConstants';

const PORTIONS = [
  { divisor: 1, fr: 'Portion entière', en: 'Full portion', pct: '100%' },
  { divisor: 2, fr: 'La moitié', en: 'Half', pct: '50%' },
  { divisor: 3, fr: 'Un tiers', en: 'A third', pct: '33%' },
];

/* ---------- Popup de log (portion + repas + date) ---------- */
const LogPortionModal = ({ recipe, en, api, onClose, onLogged }) => {
  const [divisor, setDivisor] = useState(1);
  const [meal, setMeal] = useState('lunch');
  const [date, setDate] = useState(todayStr());
  const [busy, setBusy] = useState(false);

  const m = recipe.macros;
  const scaled = {
    calories: m.calories / divisor, protein: m.protein / divisor, carbs: m.carbs / divisor, fat: m.fat / divisor,
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api(`/api/recipes/${recipe.id}/log`, {
        method: 'POST',
        body: JSON.stringify({ date, meal, portion_divisor: divisor }),
      });
      toast.success(en ? `${Math.round(scaled.calories)} kcal logged` : `${Math.round(scaled.calories)} kcal ajoutées à votre suivi`);
      onLogged?.();
      onClose();
    } catch {
      toast.error(en ? 'Could not log this recipe' : "Impossible d'ajouter cette recette");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[220] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="recipe-log-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">{en ? 'Add to my tracking' : 'Ajouter à mon suivi'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="recipe-log-close"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-sm text-zinc-400 mb-2">{en ? 'Portion eaten' : 'Portion consommée'}</p>
        <div className="grid grid-cols-3 gap-2 mb-5">
          {PORTIONS.map((p) => (
            <button
              key={p.divisor} onClick={() => setDivisor(p.divisor)}
              className={`rounded-xl border p-3 text-center transition-colors ${divisor === p.divisor ? 'bg-primary/20 border-primary text-white' : 'bg-zinc-800/60 border-white/10 text-zinc-400 hover:border-primary/40'}`}
              data-testid={`portion-${p.divisor}`}
            >
              <p className="font-bold text-sm">{p.pct}</p>
              <p className="text-[10px] mt-0.5 leading-tight">{en ? p.en : p.fr}</p>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 text-xs text-zinc-400 mb-5 rounded-xl bg-zinc-800/50 border border-white/10 p-3" data-testid="portion-macro-preview">
          <span className="font-bold text-white text-sm">{Math.round(scaled.calories)} kcal</span>
          <span>P {Math.round(scaled.protein)}g</span>
          <span>G {Math.round(scaled.carbs)}g</span>
          <span>L {Math.round(scaled.fat)}g</span>
        </div>

        <p className="text-sm text-zinc-400 mb-2">{en ? 'Meal' : 'Repas'}</p>
        <div className="grid grid-cols-3 gap-2 mb-5">
          {MEALS.map((mm) => (
            <button
              key={mm.key} onClick={() => setMeal(mm.key)}
              className={`flex items-center justify-center gap-1.5 rounded-xl border py-2.5 text-xs font-semibold transition-colors ${meal === mm.key ? 'bg-primary/20 border-primary text-white' : 'bg-zinc-800/60 border-white/10 text-zinc-400 hover:border-primary/40'}`}
              data-testid={`meal-pick-${mm.key}`}
            >
              <mm.icon className="w-3.5 h-3.5" /> {en ? mm.en : mm.fr}
            </button>
          ))}
        </div>

        <p className="text-sm text-zinc-400 mb-2">{en ? 'Date' : 'Date'}</p>
        <input
          type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="w-full bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none mb-5"
          data-testid="recipe-log-date"
        />

        <button
          onClick={confirm} disabled={busy}
          className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="recipe-log-confirm"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {en ? 'Confirm' : 'Confirmer'}
        </button>
      </motion.div>
    </motion.div>
  );
};

/* ---------- Partage (lien + WhatsApp, invite un ami à rejoindre Suivi Pro) ---------- */
const ShareModal = ({ recipe, en, api, onClose, onShared }) => {
  const url = `${window.location.origin}/suivi-pro`;
  const text = en
    ? `I just found the recipe "${recipe.name_en}" on Body-Up (${Math.round(recipe.macros.calories)} kcal, ${Math.round(recipe.macros.protein)}g protein)! Join Suivi Pro to unlock it: ${url}`
    : `Je viens de découvrir la recette "${recipe.name_fr}" sur Body-Up (${Math.round(recipe.macros.calories)} kcal, ${Math.round(recipe.macros.protein)}g de protéines) ! Rejoins Suivi Pro pour y accéder : ${url}`;
  const waLink = `https://wa.me/?text=${encodeURIComponent(text)}`;

  const markShared = () => {
    onShared(recipe.id);
    api(`/api/recipes/${recipe.id}/share`, { method: 'POST' }).catch(() => {});
  };

  const copyText = () => {
    if (!navigator.clipboard) { toast.error(en ? 'Copy failed' : 'Copie impossible'); return; }
    navigator.clipboard.writeText(text).then(
      () => { toast.success(en ? 'Text copied' : 'Texte copié dans le presse-papiers'); markShared(); },
      () => toast.error(en ? 'Copy failed' : 'Copie impossible'),
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[240] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="share-recipe-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-sm bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Share2 className="w-5 h-5 text-primary" /> {en ? 'Share this recipe' : 'Partager cette recette'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="share-recipe-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">{en ? 'Invite a friend to discover Suivi Pro.' : 'Invitez un ami à découvrir Suivi Pro.'}</p>
        <a
          href={waLink} target="_blank" rel="noopener noreferrer" onClick={markShared}
          className="w-full mb-2.5 py-3 rounded-xl font-bold text-white bg-[#25D366] hover:brightness-95 transition flex items-center justify-center gap-2"
          data-testid="share-whatsapp-btn"
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
        <button
          onClick={copyText}
          className="w-full py-3 rounded-xl font-bold text-white bg-zinc-800 border border-white/10 hover:border-primary/40 transition flex items-center justify-center gap-2"
          data-testid="share-copy-btn"
        >
          <Copy className="w-4 h-4" /> {en ? 'Copy text' : 'Copier le texte'}
        </button>
      </motion.div>
    </motion.div>
  );
};

/* ---------- Note personnelle ---------- */
const NoteSection = ({ recipe, en, api, onNoteSaved }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(recipe.my_note || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setText(recipe.my_note || ''); }, [recipe.id, recipe.my_note]);

  const save = async () => {
    setBusy(true);
    try {
      const trimmed = text.trim();
      if (trimmed) await api(`/api/recipes/${recipe.id}/note`, { method: 'PUT', body: JSON.stringify({ note: trimmed }) });
      else await api(`/api/recipes/${recipe.id}/note`, { method: 'DELETE' });
      onNoteSaved(recipe.id, trimmed);
      toast.success(en ? 'Note saved' : 'Note enregistrée');
      setEditing(false);
    } catch {
      toast.error(en ? 'Could not save note' : "Impossible d'enregistrer la note");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4" data-testid="recipe-note-section">
      <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-1.5"><NotebookPen className="w-4 h-4 text-primary" /> {en ? 'My personal note' : 'Ma note personnelle'}</h4>
      {editing ? (
        <>
          <textarea
            value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={500}
            placeholder={en ? 'e.g. swap chicken for turkey…' : 'ex : remplacer le poulet par de la dinde…'}
            className="w-full bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none resize-none"
            data-testid="recipe-note-textarea"
          />
          <div className="flex gap-2 mt-2">
            <button onClick={save} disabled={busy} className="flex-1 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-60 flex items-center justify-center gap-1.5" data-testid="recipe-note-save-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {en ? 'Save' : 'Enregistrer'}
            </button>
            <button onClick={() => { setEditing(false); setText(recipe.my_note || ''); }} className="px-4 py-2 rounded-xl text-sm font-semibold text-zinc-400 border border-white/10" data-testid="recipe-note-cancel-btn">
              {en ? 'Cancel' : 'Annuler'}
            </button>
          </div>
        </>
      ) : recipe.my_note ? (
        <div className="rounded-xl bg-zinc-800/60 border border-white/10 p-3 flex items-start justify-between gap-3">
          <p className="text-sm text-zinc-300">{recipe.my_note}</p>
          <div className="flex gap-1.5 shrink-0">
            <button onClick={() => setEditing(true)} className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="recipe-note-edit-btn"><NotebookPen className="w-4 h-4" /></button>
            <button onClick={async () => {
              if (!window.confirm(en ? 'Delete this note?' : 'Supprimer cette note ?')) return;
              try { await api(`/api/recipes/${recipe.id}/note`, { method: 'DELETE' }); onNoteSaved(recipe.id, ''); toast.success(en ? 'Note removed' : 'Note supprimée'); } catch { toast.error(en ? 'Could not remove note' : 'Impossible de supprimer la note'); }
            }} className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="recipe-note-delete-btn"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      ) : (
        <button onClick={() => setEditing(true)} className="text-sm text-primary hover:text-pink-400 font-semibold" data-testid="recipe-note-add-btn">
          + {en ? 'Add a personal note' : 'Ajouter une note personnelle'}
        </button>
      )}
    </div>
  );
};

/* ---------- Fiche détaillée ---------- */
export const RecipeDetail = ({ recipe, en, api, authFetch, onClose, isFavorite, onToggleFavorite, onNoteSaved, onRecipeLogged, onShared, allRecipes, onSwitchRecipe }) => {
  const [logging, setLogging] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !logging && !sharing) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [logging, sharing, onClose]);
  const m = recipe.macros;
  const steps = en ? recipe.steps_en : recipe.steps_fr;
  const dn = (i) => (en ? i.en : i.fr);
  const similar = (allRecipes || [])
    .filter((r) => r.id !== recipe.id)
    .map((r) => ({ r, score: Math.abs(r.macros.calories - m.calories) / 50 + Math.abs(r.macros.protein - m.protein) / 5 }))
    .sort((a, b) => a.score - b.score)
    .slice(0, 3)
    .map((x) => x.r);

  const downloadRecipePdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await authFetch(`/api/recipes/${recipe.id}/pdf${en ? '?lang=en' : ''}`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recette-${recipe.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error(en ? 'Could not download the PDF' : 'Impossible de télécharger le PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[210] bg-black/85 backdrop-blur-sm overflow-y-auto"
      onClick={onClose} data-testid="recipe-detail-modal"
    >
      <div className="min-h-full flex items-end sm:items-center justify-center p-0 sm:p-4">
        <motion.div
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full sm:max-w-xl bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col"
        >
          <div className="relative h-56 sm:h-64 shrink-0">
            <img src={recipe.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-black/30 to-black/40" />
            <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors" data-testid="recipe-detail-close">
              <X className="w-5 h-5" />
            </button>
            <div className="absolute top-3 left-3 flex items-center gap-2">
              <button
                onClick={() => onToggleFavorite(recipe.id)}
                className="w-9 h-9 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
                data-testid="recipe-detail-favorite-btn"
              >
                <Heart className={`w-4.5 h-4.5 ${isFavorite ? 'text-primary fill-primary' : 'text-white'}`} />
              </button>
              <button
                onClick={() => setSharing(true)}
                className="w-9 h-9 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
                data-testid="recipe-detail-share-btn"
              >
                <Share2 className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={downloadRecipePdf} disabled={downloadingPdf}
                className="w-9 h-9 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors disabled:opacity-60"
                data-testid="recipe-detail-download-pdf-btn"
              >
                {downloadingPdf ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Download className="w-4 h-4 text-white" />}
              </button>
              {recipe.is_popular && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-yellow-500/90 text-black" data-testid="recipe-detail-popular-badge">
                  <TrendingUp className="w-3 h-3" /> {en ? 'Popular' : 'Populaire'}
                </span>
              )}
            </div>
            <div className="absolute bottom-4 left-4 right-4">
              <h3 className="text-white text-xl sm:text-2xl font-black leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{en ? recipe.name_en : recipe.name_fr}</h3>
              <div className="flex items-center gap-3 mt-2 text-xs text-zinc-300">
                <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {recipe.prep_min} min</span>
                <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {recipe.servings} {en ? (recipe.servings > 1 ? 'servings' : 'serving') : (recipe.servings > 1 ? 'portions' : 'portion')}</span>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5 sm:p-6">
            <div className="grid grid-cols-4 gap-2 mb-6" data-testid="recipe-macros-full">
              {[
                ['calories', Flame, 'kcal', m.calories, 'text-primary'],
                ['protein', Beef, 'g', m.protein, 'text-pink-400'],
                ['carbs', Wheat, 'g', m.carbs, 'text-yellow-400'],
                ['fat', Droplet, 'g', m.fat, 'text-emerald-400'],
              ].map(([k, Icon, unit, val, color]) => (
                <div key={k} className="rounded-xl bg-zinc-800/60 border border-white/10 p-2.5 text-center">
                  <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
                  <p className="font-bold text-white text-sm">{Math.round(val)}{unit === 'kcal' ? '' : 'g'}</p>
                  <p className="text-[10px] text-zinc-500">{unit === 'kcal' ? 'kcal' : ''}</p>
                </div>
              ))}
            </div>

            <div className="mb-6">
              <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-1.5"><ListChecks className="w-4 h-4 text-primary" /> {en ? 'Ingredients' : 'Ingrédients'}</h4>
              <ul className="space-y-1.5" data-testid="recipe-ingredients">
                {recipe.ingredients.map((ing, i) => (
                  <li key={i} className="flex items-center justify-between text-sm text-zinc-300 border-b border-white/5 pb-1.5">
                    <span>{dn(ing)}</span>
                    <span className="text-zinc-500 text-xs">{ing.qty} {ing.unit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4">
              <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-1.5"><ChefHat className="w-4 h-4 text-primary" /> {en ? 'Steps' : 'Préparation'}</h4>
              <ol className="space-y-3" data-testid="recipe-steps">
                {steps.map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm text-zinc-300">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-primary/20 border border-primary/40 text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="pt-0.5">{s}</span>
                  </li>
                ))}
              </ol>
            </div>

            <NoteSection recipe={recipe} en={en} api={api} onNoteSaved={onNoteSaved} />

            {similar.length > 0 && (
              <div className="mt-2" data-testid="similar-recipes-section">
                <h4 className="text-sm font-bold text-white mb-1 flex items-center gap-1.5"><Utensils className="w-4 h-4 text-primary" /> {en ? 'Similar recipes' : 'Recettes similaires'}</h4>
                <p className="text-[11px] text-zinc-500 mb-3">{en ? 'Closest macros, any category' : 'Macros les plus proches, toutes catégories'}</p>
                <div className="grid grid-cols-3 gap-2">
                  {similar.map((r) => (
                    <button
                      key={r.id} onClick={() => onSwitchRecipe(r)}
                      className="text-left rounded-xl overflow-hidden border border-white/10 hover:border-primary/50 transition-colors bg-zinc-800/40"
                      data-testid={`similar-recipe-${r.id}`}
                    >
                      <div className="w-full aspect-square overflow-hidden">
                        <img src={r.image_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                      </div>
                      <p className="text-[11px] font-semibold text-white p-1.5 line-clamp-2 leading-snug">{en ? r.name_en : r.name_fr}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-4 sm:p-5 border-t border-white/10 shrink-0">
            <button
              onClick={() => setLogging(true)}
              className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform flex items-center justify-center gap-2"
              data-testid="recipe-add-to-tracking-btn"
            >
              <Plus className="w-4 h-4" /> {en ? 'Add to my tracking' : 'Ajouter à mon suivi'}
            </button>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {logging && <LogPortionModal recipe={recipe} en={en} api={api} onClose={() => setLogging(false)} onLogged={() => { onRecipeLogged?.(); onClose(); }} />}
      </AnimatePresence>
      <AnimatePresence>
        {sharing && <ShareModal recipe={recipe} en={en} api={api} onClose={() => setSharing(false)} onShared={onShared} />}
      </AnimatePresence>
    </motion.div>
  );
};

export default RecipeDetail;
